import { LoadingAnimationManager } from 'library/ts/managers/loading_animation/LoadingAnimationManager';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { CSSValue } from 'library/ts/ui/enums/css/CSSValue';
import ResizeObserverPolyfill from 'resize-observer-polyfill/dist/ResizeObserver.es';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { PlayerType } from 'tag/ts/tag/model/type/PlayerType';
import { TagDomAttribute } from 'tag/ts/tag/model/type/TagDomAttribute';
import { AdBlockerManager } from 'tag/ts/tag/view/AdBlockerManager';
import { ViewLibrary } from 'tag/ts/tag/view/ViewLibrary';
export class TagView extends AbstractTagClass {
    #container;
    #loadingAnimationManager;
    #adBlockManager;
    #skin;
    constructor(argument) {
        super({
            ...argument,
            name: 'TagView'
        });
        this.utils.Delay.delay().then(() => {
            this.tagEventsHandler(this.#onTagEvent, [
                TagEvent.ON_PLAYER_EVENT,
                TagEvent.ON_VIEW_READY
            ]);
            this.#createSmartTagSkeleton();
            if (this?.params?.SYSTEM?.embedType !== EmbedType.HTML5) {
                this.#setupResizeObserver();
            }
            this.dispatchTagEvent(new TagEventVO(TagEvent.ON_VIEW_READY, this.tag, undefined, this, this.name));
        });
    }
    get container() {
        return this.#container;
    }
    get smartTagDOM() {
        return {
            smartTagContent: this.container.querySelector('[' + TagDomAttribute.CONTENT + '="' + this.uuid + '"]'),
            smartTag: this.container.querySelector('[' + TagDomAttribute.SMART_TAG + '="' + this.uuid + '"]'),
            smartTagOverlay: this.container.querySelector('[' + TagDomAttribute.OVERLAY + '="' + this.uuid + '"]')
        };
    }
    get smartTagContentElement() {
        return this.container.querySelector('[' + TagDomAttribute.CONTENT + '="' + this.uuid + '"]');
    }
    get smartTagElement() {
        return this.container.querySelector('[' + TagDomAttribute.SMART_TAG + '="' + this.uuid + '"]');
    }
    get smartTagOverlayElement() {
        return this.container.querySelector('[' + TagDomAttribute.OVERLAY + '="' + this.uuid + '"]');
    }
    get loadingAnimationManager() {
        return this.#loadingAnimationManager;
    }
    get playerType() {
        let output = PlayerType.CHROMELESS;
        switch (this.params.SYSTEM.embedType) {
            case EmbedType.DESKTOP:
            case EmbedType.MOBILE_WEB:
                output = (this.capabilities.isDesktop ? PlayerType.CHROMELESS : PlayerType.FULL);
                if (this.params.SKIN.defaultSkin === true)
                    output = PlayerType.FULL;
                break;
            case EmbedType.HTML5:
                output = PlayerType.CHROMELESS;
                break;
        }
        return output;
    }
    get skin() {
        return this.#skin;
    }
    set skin(value) {
        this.#skin = value;
    }
    #disableLoadingAnimationBackgroundImage = () => {
        if (this.utils.Type.isFunction(this.loadingAnimationManager?.disableBackgroundImage)) {
            this.loadingAnimationManager.disableBackgroundImage();
        }
    };
    #setLoadingAnimationBackgroundImage = (imageURL) => {
        if (this.utils.Type.isFunction(this.loadingAnimationManager?.setBackgroundImage)) {
            this.loadingAnimationManager.setBackgroundImage(imageURL);
        }
    };
    #disableLoadingAnimationFadeIn = () => {
        if (this.utils.Type.isFunction(this.loadingAnimationManager?.disableFadeIn)) {
            this.loadingAnimationManager.disableFadeIn();
        }
    };
    #createSmartTagSkeleton = () => {
        this.#container = this.tag.params.TAG.container;
        this.#container.setAttribute(TagDomAttribute.SMART_TAG_PARENT, this.uuid);
        this.#container.appendChild(this.utils.DOM.stringToElement(ViewLibrary.SMART_TAG_SKELETON.replace(/\$UUID\$/g, this.uuid)));
        this.#setSmartTagDOMSkeletonBackground();
        if (this?.params?.TAG?.loadingAnimation === true) {
            this.#setLoadingAnimation();
        }
    };
    #setLoadingAnimation = () => {
        const loadingAnimationTagConfig = this?.params?.TAG?.loadingAnimationConfig;
        this.#loadingAnimationManager = new LoadingAnimationManager('smart-tag-loading-animation', this.tag.params.TAG.container.querySelector(('[' + TagDomAttribute.LOADING_ANIMATION + '=' + this.tag.uuid + ']')), this.utils, this.capabilities, this?.params?.TAG?.loadingAnimation, loadingAnimationTagConfig);
        if (loadingAnimationTagConfig?.backgroundImage?.enabled
            &&
                (this?.params?.SYSTEM?.embedType === EmbedType.DESKTOP
                    ||
                        this?.params?.SYSTEM?.embedType === EmbedType.MOBILE_WEB)) {
            if (this?.apiMetadata?.isLive) {
                this.#disableLoadingAnimationBackgroundImage();
            }
            else {
                if (!this.utils.URL.valid(loadingAnimationTagConfig?.backgroundImage?.imageURL)
                    &&
                        this.utils.URL.valid(this?.apiMetadata?.thumbnail)) {
                    this.#setLoadingAnimationBackgroundImage(this?.apiMetadata?.thumbnail);
                }
            }
        }
        this.utils.Delay.delay(undefined)
            .then(() => {
            if (this.utils.Type.isFunction(this?.loadingAnimationManager?.show)) {
                this.loadingAnimationManager.show();
            }
        });
    };
    #setSmartTagDOMSkeletonBackground = () => {
        const domObjectsToProcess = [
            {
                element: this.smartTagElement,
                color: this?.params?.TAG?.background?.smartTag?.color
            },
            {
                element: this.smartTagContentElement,
                color: this?.params?.TAG?.background?.content?.color
            },
            {
                element: this.smartTagOverlayElement,
                color: this?.params?.TAG?.background?.overlay?.color
            }
        ];
        domObjectsToProcess.forEach((obj) => {
            if (!this.utils.Type.isEmpty(obj.color) && this.utils.Type.isString(obj.color)) {
                if (this.utils.Type.isElement(obj.element)) {
                    obj.element.style.removeProperty('background-color');
                    obj.element.style.setProperty('background-color', obj.color.trim(), CSSValue.IMPORTANT);
                }
            }
        });
    };
    #onResizeObserverEvent = (event) => {
        this.dispatchTagEvent(this.getTagEventVO(TagEvent.ON_RESIZE, this.tag, {
            resize: event
        }, this, this.name));
    };
    #setupResizeObserver = () => {
        const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;
        const resizeObserver = new ResizeObserver((entries, observer) => {
            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                const target = entry.target;
                const rect = entry.contentRect;
                setTimeout(this.#onResizeObserverEvent, undefined, entry);
            }
        });
        resizeObserver.observe(this.#container.querySelector('[' + TagDomAttribute.SMART_TAG + '=' + this.uuid + ']'));
    };
    #onPlayerEvent = (event) => {
        const showLoadingAnimation = () => {
            if (this.capabilities.isDesktop === true && this.utils.Type.isFunction(this.loadingAnimationManager?.show)) {
                this.loadingAnimationManager.backgroundOpacity(0);
                this.loadingAnimationManager.show();
            }
        };
        const hideLoadingAnimation = () => {
            if (this.utils.Type.isFunction(this.loadingAnimationManager?.hide)) {
                this.loadingAnimationManager.hide();
            }
        };
        switch (event?.type) {
            case PlayerAdapterEvent.AD_START:
            case PlayerAdapterEvent.AD_PLAYING:
            case PlayerAdapterEvent.AD_PROGRESS:
            case PlayerAdapterEvent.CONTENT_START:
                this.#disableLoadingAnimationBackgroundImage();
                this.#disableLoadingAnimationFadeIn();
            case PlayerAdapterEvent.CONTENT_PLAYING:
            case PlayerAdapterEvent.CONTENT_PROGRESS:
                hideLoadingAnimation();
                break;
            case PlayerAdapterEvent.AD_BREAK_START:
            case PlayerAdapterEvent.AD_BREAK_COMPLETE:
            case PlayerAdapterEvent.RESOURCE_BUFFERING:
            case PlayerAdapterEvent.SEEK_START:
                showLoadingAnimation();
                break;
        }
    };
    #onTagEvent = (event) => {
        switch (event?.type) {
            case TagEvent.ON_VIEW_READY:
                this.removeTagEventsHandler(this.#onTagEvent, this?.tag?.eventDispatcher, [TagEvent.ON_VIEW_READY]).then((data) => {
                    if (this.params.SYSTEM.embedType !== EmbedType.HTML5) {
                        this.#adBlockManager = new AdBlockerManager({ tag: this.tag });
                    }
                });
                break;
            case TagEvent.ON_PLAYER_EVENT:
                this.#onPlayerEvent(event?.data?.playerAdapterEvent);
                break;
        }
    };
}
